import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { supabase } from '../../lib/supabase';

// Type matching organizations table structure + potentially added fields
type Organization = {
    id: string;
    name: string;
    type: string;
    wallet_balance: number;
    management_fee_due: number;
    monthly_management_fee_rate: number | null;
    created_at: string;
};

export function OrganizationListPage() {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const fetchOrganizations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch all orgs - assuming RLS allows superadmin
            const { data, error: fetchError } = await supabase
                .from('organizations')
                .select('id, name, type, wallet_balance, management_fee_due, monthly_management_fee_rate, created_at')
                .order('name', { ascending: true });

            if (fetchError) throw fetchError;
            setOrganizations(data || []);
        } catch (err: any) {
            console.error("Error fetching organizations:", err);
            setError(err.message || 'Failed to load organizations.');
            setOrganizations([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    const formatCurrency = (amount: number | null | undefined) => {
        if (amount === null || amount === undefined) return '-';
        return `R${amount.toFixed(2)}`;
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}> {/* Wider container */} 
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Organizations
                </Typography>
                <Box>
                     <Tooltip title="Refresh List">
                        <span>
                           <IconButton onClick={fetchOrganizations} disabled={loading}>
                             <RefreshIcon />
                           </IconButton>
                         </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/admin/organizations/new')} 
                        sx={{ ml: 1 }}
                    >
                        New Organization
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Wallet Balance</TableCell>
                                    <TableCell align="right">Fees Due</TableCell>
                                    <TableCell align="right">Fee Rate/SIM</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No organizations found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    organizations.map((org) => (
                                        <TableRow key={org.id} hover>
                                            <TableCell>{org.name}</TableCell>
                                            <TableCell>{org.type}</TableCell>
                                            <TableCell align="right">{formatCurrency(org.wallet_balance)}</TableCell>
                                            <TableCell align="right">{formatCurrency(org.management_fee_due)}</TableCell>
                                            <TableCell align="right">
                                                {org.monthly_management_fee_rate === null || org.monthly_management_fee_rate === undefined 
                                                    ? <Tooltip title="Using system default"><Typography variant="caption" color="text.secondary">Default</Typography></Tooltip>
                                                    : formatCurrency(org.monthly_management_fee_rate)}
                                            </TableCell>
                                            <TableCell>{new Date(org.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Edit Organization">
                                                    <IconButton 
                                                        size="small"
                                                        component={RouterLink}
                                                        to={`/admin/organizations/${org.id}/edit`}
                                                    >
                                                        <EditIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                {/* Add Delete button/logic here if needed later */}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Container>
    );
} 